import UserContentBookmark from '../UserContentBookmark';
import UserContentCopiedText from '../UserContentCopiedText';
import UserContentFlashcardDeck from '../UserContentFlashcardDeck';
import UserContentFlashcard from '../UserContentFlashcard';
import UserContentHighlight from '../UserContentHighlight';
import UserContentNote from '../UserContentNote';
import UserContentOfflineSession from '../UserContentOfflineSession';
import {
  USER_CONTENT_TYPES_WITH_LOCATIONS,
  BOOK_DELIMITER_TYPES,
  EPUB_ANNOTATION_PRESENTATION,
} from 'constants';
import {
  getLocalSettingsProxy,
  normalizeUserContentProperties,
} from 'utilities';

/**
 * One object representing all of the user's personal content for a given book.
 *
 * The purpose of this class is to normalize the user content items from the GraphQL response
 * and compute organized maps and derivative values.
 *
 * HOW TO USE THE ORGANIZED MAPS OF USER CONTENT
 *
 * Check out Map() here: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Map
 * Map isn't widely used because IE<11 did not support it, but it is useful here because we want to associate
 * user content with book model sub-objects without modifying those book model sub-objects. See note in ../Book/Book.js
 *
 * Some examples:
 *
 * // Iterate an organized map of flashcards:
 * for (let [marker, flashcards] of userContent.flashcardsByTopLevelMarker.entries()) {
 *   if (marker === null) {
 *     // this flashcard does not exist within any marker
 *   }
 *
 *   if (!flashcards.length) {
 *     // this marker has no associated flashcards
 *   }
 * }
 *
 * // Or iterate a list of top level markers (to control ordering and handle null separately)
 * for (let [marker] of book.topLevelMarkers) {
 *   const notes = userContent.notesByTopLevelMarker.get(marker);
 *
 *   if (!notes.length) {
 *     // this marker has no notes
 *   }
 * }
 *
 * // Get a list of top level markers that have any highlights
 * Array.from(userContent.highlightsByTopLevelMarker.entries())
 *   .filter(([marker, notes]) => notes.length)
 *   .map(([marker, notes]) => marker);
 *
 */

class UserContent {
  #copiedCharacterCount = 0;
  #deviceUUID;

  #book = null;
  #highlights = {};
  #notes = {};
  #bookmarks = {};
  #copiedTexts = {};
  #flashcards = {};
  #flashcardDecks = {};
  #offlineSessions = {};
  #offlineSessionsDataAvailable = false;

  #sortedUserContentMaps = null;

  #refetch = () => {};

  /**
   *
   * @param {Object} data - raw graphql data for user content from the backend
   * @param {Book} book - a Book instance
   * @param {String} deviceUUID - a device UUID used to filter user content to a relevant device
   * @param {Array} additionalUserContentItems - include additional user content items in the collection
   */
  constructor({
    book,
    data = {},
    deviceUUID,
    previousData,
    additionalUserContentItems = [],
    refetch = () => {},
  }) {
    this.#book = book;
    this.#copiedCharacterCount = 0;
    this.#refetch = refetch;
    this.#deviceUUID = deviceUUID ?? getLocalSettingsProxy().deviceUUID; // fall back on localSettings default for testing, storybook

    const epubAnnotationInDocumentEdges =
      data.epubAnnotationsInDocument?.edges ?? [];
    const epubAnnotationFromSharesInDocumentEdges =
      data.epubAnnotationsFromSharesInDocument?.edges ?? [];
    const epubAnnotationNotInDocumentEdges =
      data.epubAnnotationsNotInDocument?.edges ?? [];
    const epubAnnotationFromSharesNotInDocumentEdges =
      data.epubAnnotationsFromSharesNotInDocument?.edges ?? [];

    const epubAnnotations = data?.annotationsData ?? null;
    const epubAnnotationsFromShares = data?.annotationsFromSharesData ?? null;

    // try offline data first
    let epubAnnotationEdges = [].concat(
      epubAnnotations,
      epubAnnotationsFromShares,
    );

    // if merged annotations are not available, fall back on the unmerged annotations
    if (epubAnnotations === null || epubAnnotationsFromShares === null) {
      epubAnnotationEdges = [].concat(
        epubAnnotationInDocumentEdges,
        epubAnnotationFromSharesInDocumentEdges,
        epubAnnotationNotInDocumentEdges,
        epubAnnotationFromSharesNotInDocumentEdges,
      );
    }

    const epubBookmarkEdges = data.epubBookmarksByEpub?.edges ?? [];
    const copiedTextEdges = data.copiedTextsByEpub?.edges ?? [];
    const flashcardDeckEdges = data.flashcardDecksByEpub?.edges ?? [];
    const flashcardEdges = data.flashcardsByEpub?.edges ?? [];
    const offlineSessionEdges = data.offlineSessionsByEpub?.edges ?? [];

    this.#offlineSessionsDataAvailable = Boolean(data.offlineSessionsByEpub);

    // Normalize the highlights and notes
    epubAnnotationEdges.forEach((edge) => {
      const isHighlightForNote =
        edge.node.presentation ===
        EPUB_ANNOTATION_PRESENTATION.HIGHLIGHT_WITH_NOTE;

      const isHighlight =
        edge.node.presentation === EPUB_ANNOTATION_PRESENTATION.HIGHLIGHT;

      const isV6noteValidInV7 =
        edge.node.presentation ===
          EPUB_ANNOTATION_PRESENTATION.HIGHLIGHT_WITH_NOTE &&
        Boolean(edge.node.comment); // If a V6 annotation does not have note text, do not make a note for it in v7.

      const isV7Note =
        edge.node.presentation === EPUB_ANNOTATION_PRESENTATION.NOTE;

      // Normalize the highlights
      if (isHighlight || isHighlightForNote) {
        let highlight =
          previousData && previousData.highlightsMap[edge.node.id];

        const isHighlightTheSame =
          highlight &&
          highlight.isPersistedOnce &&
          highlight.color === (edge.node.color ?? 'yellow');

        const isBookReconstructed = highlight?.book !== book;

        if (!highlight || !isHighlightTheSame || isBookReconstructed) {
          highlight = new UserContentHighlight(
            normalizeUserContentProperties(book, edge.node),
          );
        }
        this.#highlights[highlight.id] = highlight;
      }

      // Normalize the notes
      if (isV7Note || isV6noteValidInV7) {
        let note = previousData && previousData.notesMap[edge.node.id];

        const isNoteTheSame =
          note &&
          note.isPersistedOnce &&
          note.color === (edge.node.color ?? 'yellow') &&
          note.userText === edge.node.comment;

        const isBookReconstructed = note?.book !== book;

        if (!note || !isNoteTheSame || isBookReconstructed) {
          note = new UserContentNote(
            normalizeUserContentProperties(book, edge.node),
          );
        }

        this.#notes[note.id] = note;
      }
    });

    // Normalize the bookmarks
    epubBookmarkEdges.forEach((edge) => {
      let bookmark = previousData && previousData.bookmarksMap[edge.node.id];
      const isBookmarkTheSame =
        bookmark &&
        bookmark.isPersistedOnce &&
        bookmark.id === edge.node.id &&
        bookmark.cfi === edge.node.cfi;

      const isBookReconstructed = bookmark?.book !== book;

      if (!bookmark || !isBookmarkTheSame || isBookReconstructed) {
        bookmark = new UserContentBookmark(
          normalizeUserContentProperties(book, edge.node),
        );
      }

      if (!bookmark.page) {
        // if there is no page, it must be an older V6 bookmark that only has a CFI. Ignore it.
        return;
      }

      this.#bookmarks[bookmark.id] = bookmark;
    });

    // Normalize the copied texts
    copiedTextEdges.forEach((edge) => {
      let copiedText =
        previousData && previousData.copiedTextsMap[edge.node.id];

      const isCopiedTextTheSame =
        copiedText &&
        copiedText.isPersistedOnce &&
        copiedText.id === edge.node.id &&
        copiedText.snippet === edge.node.text;

      const isBookReconstructed = copiedText?.book !== book;

      if (!copiedText || !isCopiedTextTheSame || isBookReconstructed) {
        copiedText = new UserContentCopiedText(
          normalizeUserContentProperties(book, edge.node),
        );
      }

      this.#copiedTexts[copiedText.id] = copiedText;
    });

    // Normalize the flashcard decks
    flashcardDeckEdges.forEach((edge) => {
      let flashcardDeck =
        previousData && previousData.flashcardDecksMap[edge.node.id];

      const isFlashcardDeckTheSame =
        flashcardDeck &&
        flashcardDeck.isPersistedOnce &&
        flashcardDeck.userText === edge.node.title &&
        flashcardDeck.id === edge.node.id;

      const isBookReconstructed = flashcardDeck?.book !== book;

      if (!flashcardDeck || !isFlashcardDeckTheSame || isBookReconstructed) {
        flashcardDeck = new UserContentFlashcardDeck(
          normalizeUserContentProperties(book, edge.node),
        );
      }

      this.#flashcardDecks[flashcardDeck.id] = flashcardDeck;
    });

    // Normalize the flashcards
    flashcardEdges.forEach((edge) => {
      let flashcard = previousData && previousData.flashcardsMap[edge.node.id];

      const isFlashcardTheSame =
        flashcard &&
        flashcard.isPersistedOnce &&
        flashcard.deckKey === edge.node.flashcardDeck.id &&
        flashcard.snippet === edge.node.term &&
        flashcard.userText === edge.node.description;

      const isBookReconstructed = flashcard?.book !== book;

      if (!flashcard || !isFlashcardTheSame || isBookReconstructed) {
        flashcard = new UserContentFlashcard(
          normalizeUserContentProperties(book, edge.node, this.flashcardDecks),
        );
      }

      this.#flashcards[flashcard.id] = flashcard;
    });

    // Normalize offline sessions
    offlineSessionEdges.forEach((edge) => {
      const offlineSession = new UserContentOfflineSession(
        normalizeUserContentProperties(book, edge.node),
      );

      this.#offlineSessions[offlineSession.id] = offlineSession;
    });

    // Let additionalUserContentItems override values from graphql
    const objectTypePairs = [
      { object: this.#highlights, type: UserContentHighlight },
      { object: this.#notes, type: UserContentNote },
      { object: this.#bookmarks, type: UserContentBookmark },
      { object: this.#copiedTexts, type: UserContentCopiedText },
      { object: this.#flashcardDecks, type: UserContentFlashcardDeck },
      { object: this.#flashcards, type: UserContentFlashcard },
      { object: this.#offlineSessions, type: UserContentOfflineSession },
    ];

    // Account for additional user content items,
    // this is used to include user content from IndexedDB
    for (let item of additionalUserContentItems) {
      for (let { object, type } of objectTypePairs) {
        if (item instanceof type) {
          object[item.id ?? item.uuid] = item;

          if (item.shouldDelete) {
            delete object[item.id ?? item.uuid];
          }
        }
      }
    }

    // Sort user content by document/marker/page
    const sortedUserContentMaps = {};

    // notesAndHighlights is not a userContentType but is combined collection of notes and highlight types
    // combining and sorting notes and highlights types here to avoid having to merge them later in app
    sortedUserContentMaps.notesAndHighlights = {};
    // Initializing notesAndHighlights with map and values for each bookDelimiterType
    for (let bookDelimiterType of BOOK_DELIMITER_TYPES) {
      sortedUserContentMaps.notesAndHighlights[bookDelimiterType] = new Map();

      // Create a null entry for "not assigned to any marker/page"
      sortedUserContentMaps.notesAndHighlights[bookDelimiterType].set(null, []);

      // Initialize arrays for every other mapping
      for (let delimiterObject of book[`${bookDelimiterType}s`]) {
        sortedUserContentMaps.notesAndHighlights[bookDelimiterType].set(
          delimiterObject,
          [],
        );
      }
    }

    // Initialize and sort userContents to userContentType by bookDelimiterType buckets
    for (let userContentType of USER_CONTENT_TYPES_WITH_LOCATIONS) {
      sortedUserContentMaps[userContentType] = {};
      for (let bookDelimiterType of BOOK_DELIMITER_TYPES) {
        sortedUserContentMaps[userContentType][bookDelimiterType] = new Map();

        // Create a null entry for "not assigned to any marker/page"
        sortedUserContentMaps[userContentType][bookDelimiterType].set(null, []);

        // Initialize arrays for every other mapping
        for (let delimiterObject of book[`${bookDelimiterType}s`]) {
          sortedUserContentMaps[userContentType][bookDelimiterType].set(
            delimiterObject,
            [],
          );
        }
      }

      // Add all user content to map objects
      for (let item of Object.values(this[userContentType])) {
        for (let bookDelimiterType of BOOK_DELIMITER_TYPES) {
          sortedUserContentMaps[userContentType][bookDelimiterType]
            .get(item.bookLocation[bookDelimiterType] ?? null)
            ?.push(item);

          if (userContentType === 'notes' || userContentType === 'highlights') {
            sortedUserContentMaps.notesAndHighlights[bookDelimiterType]
              .get(item.bookLocation[bookDelimiterType] ?? null)
              ?.push(item);
          }
        }
      }
    }

    for (let copiedText of Object.values(this.#copiedTexts)) {
      this.#copiedCharacterCount += copiedText.charCount;
    }

    this.#sortedUserContentMaps = sortedUserContentMaps;

    Object.freeze(this); // brrr
    Object.values(this.#highlights).forEach(Object.freeze);
    Object.values(this.#notes).forEach(Object.freeze);
    Object.values(this.#bookmarks).forEach(Object.freeze);
    Object.values(this.#copiedTexts).forEach(Object.freeze);
    Object.values(this.#flashcards).forEach(Object.freeze);
    Object.values(this.#flashcardDecks).forEach(Object.freeze);
    Object.values(this.#offlineSessions).forEach(Object.freeze);
  }

  /**
   * returns current book
   */
  get book() {
    return this.#book;
  }

  /**
   * returns highlight id to highlight object
   */
  get highlightsMap() {
    return this.#highlights;
  }

  /**
   * returns list of highlights
   */ get highlights() {
    return Object.values(this.#highlights);
  }

  /**
   * returns note id to note object
   */
  get notesMap() {
    return this.#notes;
  }

  /**
   * returns list of notes
   */
  get notes() {
    return Object.values(this.#notes);
  }

  /**
   * returns combined list of highlights and notes
   */
  get notesAndHighlights() {
    return Object.values({ ...this.#highlights, ...this.#notes });
  }

  /**
   * returns bookmark id to bookmark object
   */
  get bookmarksMap() {
    return this.#bookmarks;
  }

  /**
   * returns list of bookmarks
   */
  get bookmarks() {
    return Object.values(this.#bookmarks);
  }

  /**
   * returns copied text id to copied text object
   */
  get copiedTextsMap() {
    return this.#copiedTexts;
  }

  /**
   * returns list of copied texts
   */
  get copiedTexts() {
    return Object.values(this.#copiedTexts);
  }

  /**
   * returns flashcard id to flashcard object
   */
  get flashcardsMap() {
    return this.#flashcards;
  }

  /**
   * returns list of flashcards
   */
  get flashcards() {
    return Object.values(this.#flashcards);
  }

  /**
   * returns list of flashcards grouped by deck
   */
  get flashcardsByDeck() {
    if (!this.flashcardDecks) {
      return null;
    }

    let flashcardsByDeck = new Map();

    // set all decks, including empty decks
    for (let deckObject of this.flashcardDecks) {
      flashcardsByDeck.set(deckObject, []);
    }

    for (let item of this.flashcards) {
      // check the uuid as plan b
      // in case the uuid -> id change has not finished yet
      const deck = this.flashcardDecks.find(
        (deck) => deck.key === item.deckKey || deck.uuid === item.deckKey,
      );
      flashcardsByDeck.get(deck).push(item);
    }
    return Array.from(flashcardsByDeck);
  }

  /**
   * returns flashcard deck id to flashcard deck object
   */
  get flashcardDecksMap() {
    return this.#flashcardDecks;
  }

  /**
   * returns list of flashcard decks
   */
  get flashcardDecks() {
    return Object.values(this.#flashcardDecks);
  }

  /**
   * returns offline session that is relevant to the current device,
   * if it exists.
   */
  get offlineSession() {
    // Return undefined if UserContent created without offlineSession data
    if (!this.#offlineSessionsDataAvailable) {
      return undefined;
    }

    // Return offlineSession if available else return null
    return (
      Object.values(this.#offlineSessions).find(
        (offlineSession) => offlineSession.device.uuid === this.#deviceUUID,
      ) ?? null
    );
  }

  /**
   * returns list of all offline sessions for this user for this book,
   * even if not for the current device.
   */
  get offlineSessions() {
    return Object.values(this.#offlineSessions);
  }

  /**
   * returns list of flashcards for given flashcard deck
   */
  getFlashcardsByDeck(deck) {
    return Object.values(this.#flashcards).filter(deck.hasFlashcard.bind(deck));
  }

  /**
   * returns flashcard deck which contains the given flashcard
   */
  getFlashcardDeckForFlashcard(flashcard) {
    return this.#flashcardDecks.find(flashcard.inFlashcardDeck.bind(flashcard));
  }

  /**
   * returns map of document to highlights
   */
  get highlightsByDocument() {
    return this.#sortedUserContentMaps['highlights']['document'];
  }

  /**
   * returns map of marker to highlights
   */
  get highlightsByMarker() {
    return this.#sortedUserContentMaps['highlights']['marker'];
  }

  /**
   * returns map of topLevelMarker to highlights
   */
  get highlightsByTopLevelMarker() {
    return this.#sortedUserContentMaps['highlights']['topLevelMarker'];
  }

  /**
   * returns map of page to highlights
   */
  get highlightsByPage() {
    return this.#sortedUserContentMaps['highlights']['page'];
  }

  /**
   * returns map of document to notes
   */
  get notesByDocument() {
    return this.#sortedUserContentMaps['notes']['document'];
  }

  /**
   * returns map of marker to notes
   */
  get notesByMarker() {
    return this.#sortedUserContentMaps['notes']['marker'];
  }

  /**
   * returns map of topLevelMarker to notes
   */
  get notesByTopLevelMarker() {
    return this.#sortedUserContentMaps['notes']['topLevelMarker'];
  }

  /**
   * returns map of page to notes
   */
  get notesByPage() {
    return this.#sortedUserContentMaps['notes']['page'];
  }

  /**
   * returns map of document to bookmarks
   */
  get bookmarksByDocument() {
    return this.#sortedUserContentMaps['bookmarks']['document'];
  }

  /**
   * returns map of marker to bookmarks
   */
  get bookmarksByMarker() {
    return this.#sortedUserContentMaps['bookmarks']['marker'];
  }

  /**
   * returns map of topLevelMarker to bookmarks
   */
  get bookmarksByTopLevelMarker() {
    return this.#sortedUserContentMaps['bookmarks']['topLevelMarker'];
  }

  /**
   * returns map of page to bookmarks
   */
  get bookmarksByPage() {
    return this.#sortedUserContentMaps['bookmarks']['page'];
  }

  /**
   * returns map of document to copiedTexts
   */
  get copiedTextsByDocument() {
    return this.#sortedUserContentMaps['copiedTexts']['document'];
  }

  /**
   * returns map of marker to copiedTexts
   */
  get copiedTextsByMarker() {
    return this.#sortedUserContentMaps['copiedTexts']['marker'];
  }

  /**
   * returns map of topLevelMarker to copiedTexts
   */
  get copiedTextsByTopLevelMarker() {
    return this.#sortedUserContentMaps['copiedTexts']['topLevelMarker'];
  }

  /**
   * returns map of page to copiedTexts
   */
  get copiedTextsByPage() {
    return this.#sortedUserContentMaps['copiedTexts']['page'];
  }

  /**
   * returns map of document to flashcards
   */
  get flashcardsByDocument() {
    return this.#sortedUserContentMaps['flashcards']['document'];
  }

  /**
   * returns map of marker to flashcards
   */
  get flashcardsByMarker() {
    return this.#sortedUserContentMaps['flashcards']['marker'];
  }

  /**
   * returns map of topLevelMarker to flashcards
   */
  get flashcardsByTopLevelMarker() {
    return this.#sortedUserContentMaps['flashcards']['topLevelMarker'];
  }

  /**
   * returns map of page to flashcards
   */
  get flashcardsByPage() {
    return this.#sortedUserContentMaps['flashcards']['page'];
  }

  /**
   * returns map of topLevelMarker to list of notes and highlights
   */
  get notesAndHighlightsByTopLevelMarker() {
    return this.#sortedUserContentMaps['notesAndHighlights']['topLevelMarker'];
  }

  /**
   * return sum total of character count in all copied texts
   */
  get copiedCharacterCount() {
    return this.#copiedCharacterCount;
  }

  /**
   *
   * @param {*} contentToRefetch type of usercontent to refetch
   * @returns object with refetched content
   */
  refetch(contentToRefetch) {
    return this.#refetch(contentToRefetch);
  }
}

export default UserContent;
